// YERİ: src/app/domains/site/sections/offering.sections.ts
//
// SUNUM BÖLÜMLERİ — ders katalogu, gönderi tarifesi, referans projeler,
// içerik üreticisi fiyat listesi ve kanalları, ev hizmetleri fiyatları,
// kreş programları.
//
// Hepsi kiracının portal kayıtlarından besleniyor; fiyatlar elle yazılmıyor.
// Sitedeki eski fiyat kiracıyı bağlayan bir taahhüde döner — tek kaynak şart.
//
// Sunucuda elenenler: sözleşme bedeli (inşaat), kapanmış dersler, pasif
// tarife satırları.

import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';
import { paraBirimi, duvarTarihi } from './community.sections';
import {
  TenantWebService, TenantCourse, TenantShippingRate, TenantConstructionProject,
  TenantRateCard, TenantChannel, TenantHomeService, TenantDaycareProgram,
} from '../core/tenant-web.service';

// ══════════════════════════════════════════════════════════════════════════
// DERS KATALOGU
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-course-catalog',
  standalone: true,
  template: `
    @if (ders().length) {
    <section id="courses" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (d of ders(); track d.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
              @if (d.code) {
                <span class="text-xs font-bold uppercase tracking-widest" style="color:var(--site-accent)">{{ d.code }}</span>
              }
              <h3 class="mt-1 text-lg font-bold text-neutral-900 dark:text-white">{{ d.name }}</h3>
              @if (d.description) {
                <p class="mt-2 line-clamp-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ d.description }}</p>
              }
              @if (d.scheduleText) {
                <p class="mt-3 text-sm text-neutral-600 dark:text-neutral-400">{{ text.t('cs.schedule') }}: {{ d.scheduleText }}</p>
              }
              <div class="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                @if (d.credits) { <span>{{ d.credits }} {{ text.t('cs.credits') }}</span> }
                @if (d.capacity) { <span>{{ text.t('cs.quota') }}: {{ d.capacity }}</span> }
              </div>
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class CourseCatalogSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);
  protected ders = signal<TenantCourse[]>([]);

  ngOnInit() {
    const id = this.clientId(); if (!id) return;
    const qty = Number(cfg(this.config(), 'count', 12)) || 12;
    this.svc.getSchoolCourses(id, qty).subscribe({
      next: (l) => this.ders.set(l), error: () => this.ders.set([]),
    });
  }
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('sd.dersler')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('sd.dersKatalogu')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
}

// ══════════════════════════════════════════════════════════════════════════
// GÖNDERİ TARİFESİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-shipping-rates',
  standalone: true,
  template: `
    @if (tarife().length) {
    <section id="rates" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <!-- Dar ekranda tablo kendi içinde kayar; sayfa yatay kaymaz. -->
        <div class="mt-10 overflow-x-auto rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
          <table class="w-full min-w-[36rem] text-sm">
            <thead>
              <tr class="border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                <th class="px-4 py-3 text-left font-semibold">{{ text.t('sh.service') }}</th>
                <th class="px-4 py-3 text-left font-semibold">{{ text.t('sh.zone') }}</th>
                <th class="px-4 py-3 text-center font-semibold">{{ text.t('sh.weight') }}</th>
                <th class="px-4 py-3 text-right font-semibold">{{ text.t('sh.price') }}</th>
              </tr>
            </thead>
            <tbody>
              @for (t of tarife(); track t.id) {
                <tr class="border-b border-neutral-100 last:border-0 dark:border-neutral-900">
                  <td class="px-4 py-3">
                    <span class="font-semibold text-neutral-900 dark:text-white">{{ t.name }}</span>
                    @if (t.serviceType) { <span class="block text-xs text-neutral-500 dark:text-neutral-400">{{ t.serviceType }}</span> }
                  </td>
                  <td class="px-4 py-3">
                    <span class="text-neutral-700 dark:text-neutral-300">{{ t.zoneName }}</span>
                    @if (t.zoneCoverage) { <span class="block text-xs text-neutral-500 dark:text-neutral-400">{{ t.zoneCoverage }}</span> }
                  </td>
                  <td class="px-4 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ agirlik(t) }}</td>
                  <td class="px-4 py-3 text-right">
                    <span class="font-bold tabular-nums" style="color:var(--site-primary)">{{ fiyat(t.price, t.currency) }}</span>
                    <!-- Ek ücret ayrı gösteriliyor: fiyata gizlice eklenmiş
                         bir kalem müşteriyi kasada şaşırtır. -->
                    @if (t.surcharge > 0) {
                      <span class="block text-xs text-neutral-500 dark:text-neutral-400">+ {{ fiyat(t.surcharge, t.currency) }} {{ text.t('sh.surcharge') }}</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </section>
    }
  `,
})
export class ShippingRatesSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);
  protected tarife = signal<TenantShippingRate[]>([]);

  ngOnInit() {
    const id = this.clientId(); if (!id) return;
    this.svc.getShippingRates(id).subscribe({
      next: (l) => this.tarife.set(l), error: () => this.tarife.set([]),
    });
  }
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('sd.tarife')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.gonderiFiyatlari')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected fiyat = (p: number, c: string | null | undefined) => paraBirimi(p, c, this.text.lang(), this.text.currencyKey());
  protected agirlik = (t: TenantShippingRate) => `${t.minWeightKg} – ${t.maxWeightKg} kg`;
}

// ══════════════════════════════════════════════════════════════════════════
// REFERANS PROJELER (İNŞAAT)
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-project-showcase',
  standalone: true,
  template: `
    @if (proje().length) {
    <section id="projects" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-6xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (p of proje(); track p.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
              @if (p.projectType) {
                <span class="text-xs font-bold uppercase tracking-widest" style="color:var(--site-accent)">{{ p.projectType }}</span>
              }
              <h3 class="mt-1 text-lg font-bold text-neutral-900 dark:text-white">{{ p.name }}</h3>
              @if (p.location) {
                <p class="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">{{ p.location }}</p>
              }
              @if (p.description) {
                <p class="mt-2 line-clamp-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ p.description }}</p>
              }
              @if (dateRange(p)) {
                <p class="mt-3 text-xs text-neutral-500 dark:text-neutral-400">{{ dateRange(p) }}</p>
              }
              <!-- Sözleşme bedeli GÖSTERİLMEZ; ilerleme yüzdesi işin
                   durumunu anlatmaya yeter. -->
              @if (p.progressPercent > 0) {
                <div class="mt-4">
                  <div class="flex items-baseline justify-between text-xs text-neutral-500 dark:text-neutral-400">
                    <span>{{ text.t('pj.progress') }}</span>
                    <span class="tabular-nums">%{{ p.progressPercent }}</span>
                  </div>
                  <div class="mt-1.5 h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
                    <div class="h-full rounded-full" [style.width.%]="p.progressPercent" style="background:var(--site-primary)"></div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class ProjectShowcaseSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);
  protected proje = signal<TenantConstructionProject[]>([]);

  ngOnInit() {
    const id = this.clientId(); if (!id) return;
    const qty = Number(cfg(this.config(), 'count', 6)) || 6;
    this.svc.getConstructionProjects(id, qty).subscribe({
      next: (l) => this.proje.set(l), error: () => this.proje.set([]),
    });
  }
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('sd.referanslar')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('sd.projelerimiz')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected dateRange = (p: TenantConstructionProject): string => {
    const b = duvarTarihi(p.startDate, this.text.lang(), false);
    const s = duvarTarihi(p.endDate, this.text.lang(), false);
    if (b && s) return `${b} — ${s}`;
    return b || s;
  };
}

// ══════════════════════════════════════════════════════════════════════════
// İÇERİK ÜRETİCİSİ: FİYAT LİSTESİ
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-rate-card',
  standalone: true,
  template: `
    @if (kalem().length) {
    <section id="ratecard" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <ul class="mt-10 divide-y divide-neutral-200 overflow-hidden rounded-2xl border border-neutral-200 bg-white dark:divide-neutral-800 dark:border-neutral-800 dark:bg-neutral-950">
          @for (k of kalem(); track k.id) {
            <li class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 p-5">
              <div class="min-w-0">
                <span class="font-semibold text-neutral-900 dark:text-white">{{ k.serviceName }}</span>
                <span class="ml-2 text-xs uppercase tracking-wide" style="color:var(--site-accent)">
                  {{ k.platform }}@if (k.contentType) {<span> · {{ k.contentType }}</span>}
                </span>
                @if (k.description) {
                  <p class="mt-1 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ k.description }}</p>
                }
              </div>
              @if (k.price > 0) {
                <span class="shrink-0 text-xl font-bold tabular-nums" style="color:var(--site-primary)">{{ fiyat(k) }}</span>
              } @else {
                <span class="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">{{ text.t('pr.askPrice') }}</span>
              }
            </li>
          }
        </ul>
      </div>
    </section>
    }
  `,
})
export class RateCardSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);
  protected kalem = signal<TenantRateCard[]>([]);

  ngOnInit() {
    const id = this.clientId(); if (!id) return;
    this.svc.getRateCard(id).subscribe({
      next: (l) => this.kalem.set(l), error: () => this.kalem.set([]),
    });
  }
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.isBirligi')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('sd.fiyatListesi')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected fiyat = (k: TenantRateCard) => paraBirimi(k.price, k.currency, this.text.lang(), this.text.currencyKey());
}

// ══════════════════════════════════════════════════════════════════════════
// İÇERİK ÜRETİCİSİ: KANALLAR
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-social-channels',
  standalone: true,
  template: `
    @if (kanal().length) {
    <section id="channels" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
        </div>

        <div class="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          @for (k of kanal(); track k.id) {
            <a [href]="k.profileUrl || null" [attr.target]="k.profileUrl ? '_blank' : null"
               [attr.rel]="k.profileUrl ? 'noopener noreferrer' : null"
               class="rounded-2xl border border-neutral-200 bg-white p-5 text-center transition dark:border-neutral-800 dark:bg-neutral-900"
               [class.hover:shadow-lg]="!!k.profileUrl" [class.cursor-default]="!k.profileUrl">
              <p class="text-xs font-bold uppercase tracking-widest" style="color:var(--site-accent)">{{ k.platform }}</p>
              <h3 class="mt-1 font-bold text-neutral-900 dark:text-white">
                {{ k.displayName || k.handle }}
                @if (k.isVerified) { <span class="ml-1 text-sky-500" [title]="text.t('ch.verified')">✓</span> }
              </h3>
              <p class="text-xs text-neutral-500 dark:text-neutral-400">{{ k.handle }}</p>
              <p class="mt-3 text-2xl font-bold tabular-nums" style="color:var(--site-primary)">{{ takipci(k) }}</p>
              <p class="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{{ text.t('ch.followers') }}</p>
              @if (k.engagementRate > 0) {
                <p class="mt-2 text-xs text-neutral-600 dark:text-neutral-400">{{ text.t('ch.engagement') }}: %{{ k.engagementRate }}</p>
              }
              @if (k.niche) {
                <span class="mt-3 inline-block rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{{ k.niche }}</span>
              }
            </a>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class SocialChannelsSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);
  protected kanal = signal<TenantChannel[]>([]);

  ngOnInit() {
    const id = this.clientId(); if (!id) return;
    this.svc.getChannels(id).subscribe({
      next: (l) => this.kanal.set(l), error: () => this.kanal.set([]),
    });
  }
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.kanallarim')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.neredeYayindayim')));

  /** Sabit 'tr-TR' YOK: binlik ayracı sitenin diline göre. */
  protected takipci = (k: TenantChannel) => k.followers.toLocaleString(this.text.lang());
}

// ══════════════════════════════════════════════════════════════════════════
// EV HİZMETLERİ FİYATLARI
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-service-pricing',
  standalone: true,
  template: `
    @if (service().length) {
    <section id="pricing" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          @for (h of service(); track h.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
              @if (h.type) {
                <span class="text-xs font-bold uppercase tracking-widest" style="color:var(--site-accent)">{{ h.type }}</span>
              }
              <h3 class="mt-1 text-lg font-bold text-neutral-900 dark:text-white">{{ h.name }}</h3>
              @if (h.description) {
                <p class="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ h.description }}</p>
              }
              @if (h.basePrice > 0) {
                <p class="mt-4 text-2xl font-bold tabular-nums" style="color:var(--site-primary)">
                  {{ fiyat(h) }}
                  <!-- Birim şart: "₺500" tek başına neyin fiyatı olduğunu
                       söylemez — saat mi, m² mi, adet mi? -->
                  @if (h.unit) {
                    <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">/ {{ h.unit }}</span>
                  }
                </p>
              } @else {
                <p class="mt-4 text-sm text-neutral-500 dark:text-neutral-400">{{ text.t('pr.askPrice') }}</p>
              }
              @if (h.estimatedMinutes > 0) {
                <p class="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{{ text.t('sp.approx') }} {{ h.estimatedMinutes }} {{ text.t('mn.minutes') }}</p>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class ServicePricingSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);
  protected service = signal<TenantHomeService[]>([]);

  ngOnInit() {
    const id = this.clientId(); if (!id) return;
    this.svc.getHomeServices(id).subscribe({
      next: (l) => this.service.set(l), error: () => this.service.set([]),
    });
  }
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('sd.hizmetler')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('sd.hizmetlerVeFiyatlar')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected fiyat = (h: TenantHomeService) => paraBirimi(h.basePrice, h.currency, this.text.lang(), this.text.currencyKey());
}

// ══════════════════════════════════════════════════════════════════════════
// KREŞ PROGRAMLARI
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-daycare-programs',
  standalone: true,
  template: `
    @if (program().length) {
    <section id="programs" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (p of program(); track p.id) {
            <div class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
              <h3 class="text-lg font-bold text-neutral-900 dark:text-white">{{ p.name }}</h3>
              <!-- Yaş aralığı en önemli bilgi: veli önce kendi çocuğuna
                   uygun grubu arıyor. -->
              @if (ageRange(p)) {
                <p class="mt-1 text-sm font-semibold" style="color:var(--site-accent)">{{ ageRange(p) }}</p>
              }
              @if (p.description) {
                <p class="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ p.description }}</p>
              }
              @if (p.monthlyFee > 0) {
                <p class="mt-4 text-2xl font-bold tabular-nums" style="color:var(--site-primary)">
                  {{ ucret(p) }}
                  <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">{{ text.t('st.perMonth') }}</span>
                </p>
              } @else {
                <p class="mt-4 text-sm text-neutral-500 dark:text-neutral-400">{{ text.t('pr.askPrice') }}</p>
              }
              @if (p.capacity) {
                <p class="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{{ text.t('cs.quota') }}: {{ p.capacity }}</p>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class DaycareProgramsSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);
  protected program = signal<TenantDaycareProgram[]>([]);

  ngOnInit() {
    const id = this.clientId(); if (!id) return;
    this.svc.getDaycarePrograms(id).subscribe({
      next: (l) => this.program.set(l), error: () => this.program.set([]),
    });
  }
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('sd.programlar')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.yasGruplarimiz')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected ucret = (p: TenantDaycareProgram) => paraBirimi(p.monthlyFee, p.currency, this.text.lang(), this.text.currencyKey());

  /**
   * Ay cinsinden saklanıyor ama veli YIL düşünüyor: 24 ay yerine "2 yaş".
   * 24 aydan küçük değerler ay olarak bırakılıyor — bebeklikte aylık fark
   * gerçekten anlamlı.
   */
  protected ageRange = (p: TenantDaycareProgram): string => {
    const bicim = (ay: number): string =>
      ay < 24 ? `${ay} ${this.text.t('dc.months')}` : `${Math.floor(ay / 12)} ${this.text.t('dc.years')}`;

    const alt = p.ageMonthsMin, ust = p.ageMonthsMax;
    if (alt != null && ust != null) return `${bicim(alt)} – ${bicim(ust)}`;
    if (alt != null) return `${bicim(alt)}+`;
    if (ust != null) return `${this.text.t('dc.upTo')} ${bicim(ust)}`;
    return '';
  };
}
